import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { principalNavItems } from '../config/principalNav';
import { Calendar, Check, Clock, FileText, Phone, Plus, Send, Trash2, Users, Video, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalMeetings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [principalName, setPrincipalName] = useState('Principal');
  const [meetings, setMeetings] = useState([]);
  const [observers, setObservers] = useState([]);
  const [parents, setParents] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showMomModal, setShowMomModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');

  const [form, setForm] = useState({
    meeting_type: 'observer_weekly',
    title: '',
    scheduled_at: '',
    duration_minutes: 30,
    attendees: [],
    agenda: '',
    meeting_link: ''
  });

  const [momForm, setMomForm] = useState({
    key_points: [''],
    action_items: [''],
    decisions: '',
    next_steps: ''
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('principal_token');

      const [meetingsRes, observersRes, dashRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/meetings`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/observers`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/dashboard?token=${token}`).catch(() => ({ data: {} }))
      ]);

      setMeetings(meetingsRes.data.meetings || []);
      setObservers(observersRes.data.observers || []);
      setPrincipalName(dashRes.data?.principal?.name || 'Principal');

      const childrenRes = await axios.get(`${BACKEND_URL}/api/principal/children`, { params: { token } });
      const parentList = [];
      (childrenRes.data.children || []).forEach(child => {
        if (child.parent_id && !parentList.find(p => p.id === child.parent_id)) {
          parentList.push({
            id: child.parent_id,
            name: child.parent_name || `Parent of ${child.name}`,
            child_name: child.name
          });
        }
      });
      setParents(parentList);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  const scheduleMeeting = async () => {
    if (!form.title || !form.scheduled_at || form.attendees.length === 0) {
      alert('Please fill all required fields and select at least one attendee');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/meetings`, null, {
        params: {
          token,
          meeting_type: form.meeting_type,
          title: form.title,
          scheduled_at: form.scheduled_at,
          duration_minutes: form.duration_minutes,
          attendees: JSON.stringify(form.attendees),
          agenda: form.agenda,
          meeting_link: form.meeting_link
        }
      });
      alert('Meeting scheduled! Attendees have been notified.');
      setShowScheduleModal(false);
      setForm({ meeting_type: 'observer_weekly', title: '', scheduled_at: '', duration_minutes: 30, attendees: [], agenda: '', meeting_link: '' });
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to schedule meeting');
    } finally {
      setSubmitting(false);
    }
  };

  const generateMom = async () => {
    if (!selectedMeeting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/meetings/${selectedMeeting.id}/mom`, null, {
        params: {
          token,
          key_points: JSON.stringify(momForm.key_points.filter(k => k.trim())),
          action_items: JSON.stringify(momForm.action_items.filter(a => a.trim())),
          decisions: momForm.decisions,
          next_steps: momForm.next_steps,
          share_with_attendees: true
        }
      });
      alert('Minutes of Meeting generated and shared with attendees!');
      setShowMomModal(false);
      setMomForm({ key_points: [''], action_items: [''], decisions: '', next_steps: '' });
      setSelectedMeeting(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate MoM');
    } finally {
      setSubmitting(false);
    }
  };

  const completeMeeting = async (meetingId) => {
    try {
      const token = localStorage.getItem('principal_token');
      await axios.put(`${BACKEND_URL}/api/principal/meetings/${meetingId}/complete`, null, { params: { token } });
      loadData();
    } catch (error) {
      alert('Failed to mark meeting as completed');
    }
  };

  const startMeeting = async (meeting) => {
    window.open(meeting.meeting_link, '_blank', 'noopener,noreferrer');
    try {
      const token = localStorage.getItem('principal_token');
      await axios.put(`${BACKEND_URL}/api/principal/meetings/${meeting.id}/start`, null, { params: { token } });
      loadData();
    } catch (error) {
      console.error('start meeting failed', error);
    }
  };

  const cancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) return;
    try {
      const token = localStorage.getItem('principal_token');
      await axios.delete(`${BACKEND_URL}/api/principal/meetings/${meetingId}`, {
        params: { token, reason: 'Cancelled by principal' }
      });
      loadData();
    } catch (error) {
      alert('Failed to cancel meeting');
    }
  };

  const toggleAttendee = (id) => {
    if (form.attendees.includes(id)) {
      setForm({ ...form, attendees: form.attendees.filter(a => a !== id) });
    } else {
      setForm({ ...form, attendees: [...form.attendees, id] });
    }
  };

  const filteredMeetings = meetings.filter(m => {
    if (activeTab === 'upcoming') return m.status === 'scheduled' || m.status === 'live';
    if (activeTab === 'completed') return m.status === 'completed';
    if (activeTab === 'cancelled') return m.status === 'cancelled';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base sm:text-lg text-slate-600 font-medium">Loading meetings...</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveLayout
      navItems={principalNavItems}
      userRole="principal"
      userName={principalName}
      onLogout={handleLogout}
    >
      <div className="font-body" data-testid="meetings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
              <Video className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
              Meetings
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Schedule meetings with observers and parents</p>
          </div>
          <Button
            onClick={() => setShowScheduleModal(true)}
            className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark text-white self-start sm:self-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule Meeting
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
          {['upcoming', 'completed', 'cancelled'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab)}
              className={`text-xs sm:text-sm capitalize ${activeTab === tab ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
            >
              {tab}
            </Button>
          ))}
        </div>

        {/* Meetings List */}
        <div className="space-y-4">
          {filteredMeetings.length === 0 ? (
            <Card className="border-2 border-slate-200">
              <CardContent className="p-12 text-center">
                <Video className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold font-heading text-sanjaya-navy mb-2">No {activeTab} meetings</h3>
                <p className="text-slate-500 mb-4 text-sm">Schedule meetings with observers and parents</p>
                {activeTab === 'upcoming' && (
                  <Button onClick={() => setShowScheduleModal(true)} className="bg-sanjaya-navy hover:bg-sanjaya-navy-dark">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule First Meeting
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            filteredMeetings.map((meeting) => (
              <Card key={meeting.id} className="border-2 border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        meeting.meeting_type === 'observer_weekly' ? 'bg-sanjaya-navy/10' : 'bg-sanjaya-orange/20'
                      }`}>
                        {meeting.meeting_type === 'observer_weekly'
                          ? <Users className="w-5 h-5 sm:w-6 sm:h-6 text-sanjaya-navy" />
                          : <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-sanjaya-orange" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-sm sm:text-base text-slate-800 font-heading">{meeting.title}</h3>
                          {meeting.status === 'live' && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-700 animate-pulse" data-testid={`live-badge-${meeting.id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                              Live now
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                            {new Date(meeting.scheduled_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                            {new Date(meeting.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                            {meeting.attendees?.length || 0} attendees
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            meeting.meeting_type === 'observer_weekly'
                              ? 'bg-sanjaya-navy/10 text-sanjaya-navy'
                              : 'bg-sanjaya-orange/20 text-sanjaya-orange'
                          }`}>
                            {meeting.meeting_type === 'observer_weekly' ? 'Observer Meeting' : 'Parent Counseling'}
                          </span>
                        </div>
                        {meeting.agenda && (
                          <p className="text-xs sm:text-sm text-slate-600 mt-2">{meeting.agenda}</p>
                        )}
                        {meeting.mom && (
                          <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-sanjaya-teal">
                            <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                            MoM Generated
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {meeting.status === 'scheduled' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => startMeeting(meeting)}
                            className="bg-sanjaya-orange hover:bg-sanjaya-orange/90 text-sanjaya-navy font-semibold"
                            data-testid={`start-meeting-${meeting.id}`}
                          >
                            <Video className="w-4 h-4 mr-1" />
                            Start Now
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => completeMeeting(meeting.id)} className="border-sanjaya-navy/30 text-sanjaya-navy">
                            <Check className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-600 border-red-200" onClick={() => cancelMeeting(meeting.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {meeting.status === 'live' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => window.open(meeting.meeting_link, '_blank', 'noopener,noreferrer')}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid={`rejoin-meeting-${meeting.id}`}
                          >
                            <Video className="w-4 h-4 mr-1" />
                            Rejoin
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => completeMeeting(meeting.id)} className="border-sanjaya-navy/30 text-sanjaya-navy">
                            <Check className="w-4 h-4 mr-1" />
                            End meeting
                          </Button>
                        </>
                      )}
                      {meeting.status === 'completed' && !meeting.mom && (
                        <Button size="sm" onClick={() => { setSelectedMeeting(meeting); setShowMomModal(true); }} className="bg-sanjaya-teal hover:bg-sanjaya-teal/90 text-white">
                          <FileText className="w-4 h-4 mr-1" />
                          Generate MoM
                        </Button>
                      )}
                      {meeting.mom && (
                        <Button variant="outline" size="sm" onClick={() => setSelectedMeeting(meeting)} className="border-sanjaya-navy/30 text-sanjaya-navy">
                          <FileText className="w-4 h-4 mr-1" />
                          View MoM
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between font-heading text-sanjaya-navy">
                <span>Schedule Meeting</span>
                <Button variant="ghost" size="sm" onClick={() => setShowScheduleModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Meeting Type</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    onClick={() => setForm({ ...form, meeting_type: 'observer_weekly', attendees: [] })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      form.meeting_type === 'observer_weekly'
                        ? 'border-sanjaya-navy bg-sanjaya-navy/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Users className="w-6 h-6 text-sanjaya-navy mb-2" />
                    <h4 className="font-semibold text-slate-800">Weekly Observer Meeting</h4>
                    <p className="text-sm text-slate-500">Team sync with observers</p>
                  </button>
                  <button
                    onClick={() => setForm({ ...form, meeting_type: 'parent_counseling', attendees: [] })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      form.meeting_type === 'parent_counseling'
                        ? 'border-sanjaya-orange bg-sanjaya-orange/10'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Phone className="w-6 h-6 text-sanjaya-orange mb-2" />
                    <h4 className="font-semibold text-slate-800">Parent Counseling</h4>
                    <p className="text-sm text-slate-500">Fortnightly parent session</p>
                  </button>
                </div>
              </div>

              <div>
                <Label>Title</Label>
                <Input
                  placeholder="e.g., Weekly Observer Sync"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1 focus:border-sanjaya-navy"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date & Time</Label>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Duration (minutes)</Label>
                  <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) })} min={15} max={120} className="mt-1" />
                </div>
              </div>

              <div>
                <Label>Select Attendees</Label>
                <div className="mt-2 max-h-40 overflow-y-auto border-2 border-slate-200 rounded-xl p-2">
                  {form.meeting_type === 'observer_weekly' ? (
                    observers.map((obs) => (
                      <label key={obs.id} className="flex items-center gap-3 p-2 hover:bg-sanjaya-navy/5 rounded cursor-pointer">
                        <input type="checkbox" checked={form.attendees.includes(obs.id)} onChange={() => toggleAttendee(obs.id)} className="rounded" />
                        <span className="text-sm font-medium text-slate-800">{obs.name}</span>
                        <span className="text-xs text-slate-500">{obs.email}</span>
                      </label>
                    ))
                  ) : (
                    parents.map((parent) => (
                      <label key={parent.id} className="flex items-center gap-3 p-2 hover:bg-sanjaya-navy/5 rounded cursor-pointer">
                        <input type="checkbox" checked={form.attendees.includes(parent.id)} onChange={() => toggleAttendee(parent.id)} className="rounded" />
                        <span className="text-sm font-medium text-slate-800">{parent.name}</span>
                        <span className="text-xs text-slate-500">({parent.child_name})</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">{form.attendees.length} selected</p>
              </div>

              <div>
                <Label>Agenda (Optional)</Label>
                <Textarea placeholder="Meeting agenda..." value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} rows={3} className="mt-1" />
              </div>

              <div>
                <Label>Meeting Link (Optional)</Label>
                <Input placeholder="Auto-generates a Jitsi room if left empty (recommended)" value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} className="mt-1" />
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1 border-slate-300" onClick={() => setShowScheduleModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-sanjaya-navy hover:bg-sanjaya-navy-dark" onClick={scheduleMeeting} disabled={submitting}>
                  {submitting ? 'Scheduling...' : (
                    <><Calendar className="w-4 h-4 mr-2" />Schedule Meeting</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* MoM Modal */}
      {showMomModal && selectedMeeting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between font-heading text-sanjaya-navy">
                <span>Generate Minutes of Meeting</span>
                <Button variant="ghost" size="sm" onClick={() => { setShowMomModal(false); setSelectedMeeting(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-sanjaya-navy/5 p-4 rounded-xl border border-sanjaya-navy/20">
                <h4 className="font-semibold text-sanjaya-navy">{selectedMeeting.title}</h4>
                <p className="text-sm text-slate-500">{new Date(selectedMeeting.scheduled_at).toLocaleString()}</p>
              </div>

              <div>
                <Label>Key Discussion Points</Label>
                {momForm.key_points.map((point, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input placeholder="Enter key point discussed..." value={point} onChange={(e) => {
                      const newPoints = [...momForm.key_points];
                      newPoints[idx] = e.target.value;
                      setMomForm({ ...momForm, key_points: newPoints });
                    }} />
                    {momForm.key_points.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => setMomForm({ ...momForm, key_points: momForm.key_points.filter((_, i) => i !== idx) })}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setMomForm({ ...momForm, key_points: [...momForm.key_points, ''] })} className="mt-2 border-sanjaya-navy/30 text-sanjaya-navy">
                  <Plus className="w-4 h-4 mr-1" />Add Point
                </Button>
              </div>

              <div>
                <Label>Action Items</Label>
                {momForm.action_items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input placeholder="Enter action item..." value={item} onChange={(e) => {
                      const newItems = [...momForm.action_items];
                      newItems[idx] = e.target.value;
                      setMomForm({ ...momForm, action_items: newItems });
                    }} />
                    {momForm.action_items.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => setMomForm({ ...momForm, action_items: momForm.action_items.filter((_, i) => i !== idx) })}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setMomForm({ ...momForm, action_items: [...momForm.action_items, ''] })} className="mt-2 border-sanjaya-navy/30 text-sanjaya-navy">
                  <Plus className="w-4 h-4 mr-1" />Add Action
                </Button>
              </div>

              <div>
                <Label>Decisions Made</Label>
                <Textarea placeholder="Summarize key decisions..." value={momForm.decisions} onChange={(e) => setMomForm({ ...momForm, decisions: e.target.value })} rows={3} className="mt-1" />
              </div>

              <div>
                <Label>Next Steps</Label>
                <Textarea placeholder="Outline next steps and follow-ups..." value={momForm.next_steps} onChange={(e) => setMomForm({ ...momForm, next_steps: e.target.value })} rows={3} className="mt-1" />
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1 border-slate-300" onClick={() => { setShowMomModal(false); setSelectedMeeting(null); }}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-sanjaya-teal hover:bg-sanjaya-teal/90 text-white" onClick={generateMom} disabled={submitting}>
                  {submitting ? 'Generating...' : (<><Send className="w-4 h-4 mr-2" />Generate & Share MoM</>)}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View MoM Modal */}
      {selectedMeeting?.mom && !showMomModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between font-heading text-sanjaya-navy">
                <span>Minutes of Meeting</span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedMeeting(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-sanjaya-navy/10 p-4 rounded-xl">
                <h4 className="font-semibold text-sanjaya-navy font-heading">{selectedMeeting.title}</h4>
                <p className="text-sm text-slate-600">{new Date(selectedMeeting.scheduled_at).toLocaleString()}</p>
              </div>

              {selectedMeeting.mom.key_points?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-sanjaya-navy font-heading">Key Discussion Points</h4>
                  <ul className="space-y-1">
                    {selectedMeeting.mom.key_points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-700 text-sm">
                        <span className="text-sanjaya-navy font-bold mt-0.5">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedMeeting.mom.action_items?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-sanjaya-navy font-heading">Action Items</h4>
                  <ul className="space-y-1">
                    {selectedMeeting.mom.action_items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-700 text-sm">
                        <Check className="w-4 h-4 text-sanjaya-teal mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedMeeting.mom.decisions && (
                <div>
                  <h4 className="font-semibold mb-2 text-sanjaya-navy font-heading">Decisions Made</h4>
                  <p className="text-slate-700 text-sm">{selectedMeeting.mom.decisions}</p>
                </div>
              )}

              {selectedMeeting.mom.next_steps && (
                <div>
                  <h4 className="font-semibold mb-2 text-sanjaya-navy font-heading">Next Steps</h4>
                  <p className="text-slate-700 text-sm">{selectedMeeting.mom.next_steps}</p>
                </div>
              )}

              <div className="pt-4 border-t text-xs text-slate-500">
                Generated on {new Date(selectedMeeting.mom.generated_at).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </ResponsiveLayout>
  );
};

export default PrincipalMeetings;
